<template>
  <ValidationObserver v-slot="{ invalid }" slim>
    <div id="AccountSecurity" class="Main-Content">

      <LoadingLayout v-if="loading" :page="true" />

      <Page
        v-else
        :title="$t('Security')"
        :primaryAction="{
          label: $t('Update'),
          onAction: save,
          disabled: !hasChanged || canSaveError || saving || invalid
        }"
        :secondaryActions="secondaryActions"
      >

        <Layout>
          <LayoutSection>
            <Card v-if="showLogs">
              <Filters
                :filters="[
                  {
                    key: 'type',
                    label: $t('Type'),
                    type: 'select',
                    placeholder: $t('Choose a type'),
                    multiple: true,
                    options: logTypeOptions
                  }
                ]"
                :applied-filters="filters"
                :query.sync="query"
                @update="val => filters = val"
              />
              <DataTable
                :columns="columns"
                :rows="formattedLogs"
                :pagination="{
                  offset: offset,
                  limit: limit,
                  total: formattedLogs.length,
                  updateOffset: val => offset = val,
                  updateLimit: updateLimit
                }"
                :loading="logsLoading"
              />
            </Card>
            <Card :title="$t('Two Factor Authentication (2FA)')">
              <CardSection>

                <FormLayout>
                  <Select
                    v-model="twoFactorEnabled"
                    :options="[
                      { label: $t('Enabled'), value: true },
                      { label: $t('Disabled'), value: false },
                    ]"
                  />

                  <ChoiceList
                    v-if="twoFactorEnabled"
                    :label="$t('Allow users to choose the following 2FA methods')"
                    :options="methods"
                    multiple
                    :error="canSaveError"
                    v-model="accountSecurity.twoFactorMethods"
                  />
                </FormLayout>

              </CardSection>
            </Card>
            <Card :title="$t('AccountSecurity.forcePasswordChange')">
              <CardSection>
                <FormLayout>
                  <Select
                    v-model="forcePasswordChangeEnabled"
                    :options="[
                      { label: $t('Enabled'), value: true },
                      { label: $t('Disabled'), value: false }
                    ]"
                  />
                  <FormLayoutGroup v-if="forcePasswordChangeEnabled">
                    <validation-provider :name="$t('AccountSecurity.passwordValidityPeriod')" immediate rules="required|min_value:30" v-slot="{ errors }">
                      <TextField 
                        type="number"
                        :label="$t('AccountSecurity.passwordValidityPeriod')"
                        v-model="accountSecurity.securityForcePasswordChangeDays"
                        :helpText="$t('AccountSecurity.passwordValidityPeriodHelp')"
                        :min="30"
                        :error="errors[0]"
                      />
                    </validation-provider>
                    <validation-provider :name="$t('AccountSecurity.reminderDays')" immediate rules="required|min_value:1" v-slot="{ errors }">
                      <TextField 
                        type="number"
                        :label="$t('AccountSecurity.reminderDays')"
                        v-model="accountSecurity.securityForcePasswordChangeWarningDays"
                        :helpText="$t('AccountSecurity.passwordResetReminderDaysHelp')"
                        :min="1"
                        :error="errors[0] || forcePasswordChangeError"
                      />
                    </validation-provider>
                  </FormLayoutGroup>
                </FormLayout>
              </CardSection>
            </Card>
            <Card :title="$t('AccountSecurity.passwordReuseSecurity')">
              <CardSection>
                <FormLayout>
                  <Select
                    v-model="passwordReuseEnabled"
                    :options="[
                      { label: $t('Enabled'), value: true },
                      { label: $t('Disabled'), value: false }
                    ]"
                  />
                  <!-- <pre>{{accountSecurity}}</pre> -->
                  <FormLayoutGroup v-if="passwordReuseEnabled"> 
                    <validation-provider :name="$t('AccountSecurity.numberOfPasswords')" immediate rules="required|min_value:1" v-slot="{ errors }">
                      <TextField 
                        type="number"
                        :label="$t('AccountSecurity.numberOfPasswords')"
                        v-model="accountSecurity.securityPasswordReuse.disallowPreviousPasswords"
                        :helpText="$t('AccountSecurity.passwordReuseHelp')"
                        :error="errors[0]"
                        :min="1"
                      />  
                    </validation-provider>
                  </FormLayoutGroup>
                </FormLayout>
              </CardSection>
            </Card>
            <Card :title="$t('AccountSecurity.passwordComplexity')">
              <CardSection>
                <!-- <FormLayout> -->
                  <Select
                    v-model="passwordComplexityEnabled"
                    :options="[
                      { label: $t('Enabled'), value: true },
                      { label: $t('Disabled'), value: false }
                    ]"
                  />
                  <template v-if="passwordComplexityEnabled">
                    <div class="checkbox">
                      <Checkbox
                        :label="$t('AccountSecurity.requireLength')"
                        :helpText="$t('AccountSecurity.requireLengthHelp')"
                        v-model="complexityMinLengthEnabled"
                      />
                    </div>
                    <div class="checkbox">
                      <Checkbox
                        :label="$t('AccountSecurity.requireSymbol')"
                        :helpText="$t('AccountSecurity.requireSymbolHelp', { value: symbolsAllowed })"
                        v-model="accountSecurity.securityPasswordComplexity.requiresSymbol"
                      />
                    </div>
                    <div class="checkbox">
                      <Checkbox
                        :label="$t('AccountSecurity.requireNumber')"
                        :helpText="$t('AccountSecurity.requireNumberHelp')"
                        v-model="accountSecurity.securityPasswordComplexity.requiresNumber"
                      />
                    </div>
                    <div class="checkbox">
                      <Checkbox
                        :label="$t('AccountSecurity.requireUpperLower')"
                        :helpText="$t('AccountSecurity.requireUpperLowerHelp')"
                        v-model="accountSecurity.securityPasswordComplexity.requiresMixedCase"
                      />
                    </div>
                  </template>
                <!-- </FormLayout> -->
              </CardSection>
              <CardSection v-if="passwordComplexityEnabled && complexityMinLengthEnabled">
                <validation-provider :name="$t('AccountSecurity.minPasswordLength')" immediate rules="required|min_value:8" v-slot="{ errors }">
                  <TextField 
                    type="number"
                    :label="$t('AccountSecurity.minPasswordLength')"
                    v-model="accountSecurity.securityPasswordComplexity.minimumLength"
                    :min="8"
                    :error="errors[0]"
                  />
                </validation-provider>
              </CardSection>
            </Card>
            <Card :title="$t('AccountSecurity.lockAccountsFailed')">
              <CardSection>
                <FormLayout>
                  <Select
                    v-model="lockAccountsFailedEnabled"
                    :options="[
                      { label: $t('Enabled'), value: true },
                      { label: $t('Disabled'), value: false }
                    ]"
                  />
                  <FormLayoutGroup v-if="lockAccountsFailedEnabled"> 
                    <validation-provider :name="$t('AccountSecurity.numberOfAttempts')" immediate rules="required|min_value:3" v-slot="{ errors }">
                      <TextField 
                        type="number"
                        :label="$t('AccountSecurity.numberOfAttempts')"
                        v-model="accountSecurity.securityLoginFailures.numberOfFailedLogins"
                        :helpText="$t('AccountSecurity.numberOfAttemptsHelp')"
                        :min="3"
                        :error="errors[0]"
                      />
                    </validation-provider>
                    <validation-provider :name="$t('AccountSecurity.minutes')" immediate rules="required|min_value:5" v-slot="{ errors }">
                      <TextField 
                        type="number"
                        :label="$t('AccountSecurity.minutes')"
                        v-model="accountSecurity.securityLoginFailures.resetMinutes"
                        :helpText="$t('AccountSecurity.minutesFailedHelp')"
                        :min="5"
                        :error="errors[0]"
                      />
                    </validation-provider>
                  </FormLayoutGroup>
                </FormLayout>
              </CardSection>
            </Card>
            <Card :title="$t('AccountSecurity.lockAccountsInactivity')">
              <CardSection>
                <FormLayout>
                  <Select
                    v-model="lockAccountsNoLoginEnabled"
                    :options="[
                      { label: $t('Enabled'), value: true },
                      { label: $t('Disabled'), value: false }
                    ]"
                  />
                  <FormLayoutGroup v-if="lockAccountsNoLoginEnabled"> 
                    <validation-provider :name="$t('AccountSecurity.numberOfDays')" immediate rules="required|min_value:7" v-slot="{ errors }">
                      <TextField 
                        type="number"
                        :label="$t('AccountSecurity.numberOfDays')"
                        v-model="accountSecurity.securityInactivityLockDays"
                        :helpText="$t('AccountSecurity.numberOfDaysHelp')"
                        :error="errors[0]"
                        :min="7"
                      />
                    </validation-provider>
                    <validation-provider :name="$t('AccountSecurity.reminderDays')" immediate rules="required|min_value:1" v-slot="{ errors }">
                      <TextField 
                        type="number"
                        :label="$t('AccountSecurity.reminderDays')"
                        v-model="accountSecurity.securityInactivityLockWarningDays"
                        :helpText="$t('AccountSecurity.inactivityReminderDaysHelp')"
                        :error="errors[0] || lockAccountsNoLoginError"
                        :min="1"
                      />
                    </validation-provider>
                  </FormLayoutGroup>
                </FormLayout>
              </CardSection>
            </Card>
            <Card :title="$t('AccountSecurity.autoLogoutUser')">
              <CardSection>
                <FormLayout>
                  <Select
                    v-model="autoLogoutEnabled"
                    :options="[
                      { label: $t('Enabled'), value: true },
                      { label: $t('Disabled'), value: false }
                    ]"
                  />
                  <FormLayoutGroup v-if="autoLogoutEnabled"> 
                    <validation-provider :name="$t('AccountSecurity.minutes')" immediate rules="required|min_value:5" v-slot="{ errors }">
                      <TextField 
                        type="number"
                        :label="$t('AccountSecurity.minutes')"
                        v-model="accountSecurity.securityAutomaticLogout.numMinutes"
                        :helpText="$t('AccountSecurity.minutesAutoLogoutHelp')"
                        :error="errors[0]"
                        :min="5"
                      />
                    </validation-provider>
                  </FormLayoutGroup>
                </FormLayout>
              </CardSection>
            </Card>
          </LayoutSection>
        </Layout>
        <!-- <pre>{{accountSecurity}}</pre> -->
      </Page>

    </div>
  </ValidationObserver>
</template>

<script>
import _ from 'lodash'

export default {
  name: 'AccountSecurity',
  data () {
    return {
      loading: true,
      saving: false,
      showLogs: false,
      logsLoading: false,
      accountSecurity: {
        twoFactorMethods: [],
        securityPasswordReuse: {},
        securityPasswordComplexity: {},
        securityLoginFailures: {},
        securityForcePasswordChangeDays: 0,
        securityForcePasswordChangeWarningDays: 0,
        securityInactivityLockDays: 0,
        securityInactivityLockWarningDays: 0,
        securityAutomaticLogout: {}
      },
      accountSecurityCopy: {},
      twoFactorEnabled: false,
      forcePasswordChangeEnabled: false,
      passwordReuseEnabled: false,
      passwordLengthEnabled: false,
      passwordComplexityEnabled: false,
      complexityMinLengthEnabled: false,
      lockAccountsFailedEnabled: false,
      lockAccountsNoLoginEnabled: false,
      autoLogoutEnabled: false,

      symbolsAllowed: `~\`!@#$%^&*()-[]{}|_+=\\"':;<,>.?/`,

      logs: [],
      offset: 0,
      limit: 10,
      filters: {},
      query: '',

      defaults: {
        securityPasswordReuse: {
          disallowPreviousPasswords: 1
        },
        securityPasswordComplexity: {
          minimumLength: 8
        },
        securityLoginFailures: {
          numberOfFailedLogins: 3,
          resetMinutes: 5
        },
        securityAutomaticLogout: {
          numMinutes: 30
        },
        securityForcePasswordChangeDays: 90,
        securityForcePasswordChangeWarningDays: 7,
        securityInactivityLockDays: 90,
        securityInactivityLockWarningDays: 7
      }
    }
  },
  mounted() {
    if (!this.theUser || !this.theUser.isAdmin) {
      this.$router.replace({ name: 'AccountProfile' })
      return
    }
    this.limit = this.getTableLimit('tableLimit') || 10
    NProgress.start()
    this.api.getCurrentAccountSecurity()
      .then((data) => {
        this.accountSecurity = _.cloneDeep(data)
        this.accountSecurityCopy = _.cloneDeep(data)
        this.twoFactorEnabled = this.accountSecurity.twoFactorMethods.length > 0
        this.passwordReuseEnabled = !_.isEmpty(this.accountSecurity.securityPasswordReuse)
        this.passwordComplexityEnabled = !_.isEmpty(this.accountSecurity.securityPasswordComplexity)
        this.complexityMinLengthEnabled = !!this.accountSecurity.securityPasswordComplexity.minimumLength
        this.lockAccountsFailedEnabled = !_.isEmpty(this.accountSecurity.securityLoginFailures)
        this.lockAccountsNoLoginEnabled = !!this.accountSecurity.securityInactivityLockDays
        this.forcePasswordChangeEnabled = !!this.accountSecurity.securityForcePasswordChangeDays
        this.autoLogoutEnabled = !_.isEmpty(this.accountSecurity.securityAutomaticLogout)
        this.loading = false
        this.enableWatchers()
      })
      .catch(this.apiCatch) 
      .finally(()=> {
        NProgress.done()
      })

  },
  computed: {
    hasChanged() {
      return !_.isEqual(this.accountSecurity, this.accountSecurityCopy)
    },  
    canSaveError() {
      if (this.twoFactorEnabled) {
        const methods = this.accountSecurity.twoFactorMethods
        if (!methods.length || (methods.length === 1 && methods.indexOf('none') >= 0)) {
          return this.$t('AccountSecurity.notNoneMessage')
        }
      }
      return this.forcePasswordChangeError || this.lockAccountsNoLoginError
    },
    forcePasswordChangeError() {
      if (this.forcePasswordChangeEnabled) {
        const {securityForcePasswordChangeDays, securityForcePasswordChangeWarningDays} = this.accountSecurity
        if (securityForcePasswordChangeDays && securityForcePasswordChangeWarningDays > securityForcePasswordChangeDays) {
          return this.$t('AccountSecurity.forcePasswordChangeError')
        }
      }
      return false
    },
    lockAccountsNoLoginError() {
      if (this.lockAccountsNoLoginEnabled) {
        const {securityInactivityLockDays, securityInactivityLockWarningDays} = this.accountSecurity
        if (securityInactivityLockDays && securityInactivityLockWarningDays > securityInactivityLockDays) {
          return this.$t('AccountSecurity.lockAccountsNoLoginError')
        }
      }
      return false
    },
    methods() {
      return [
        { value: 'email', label: this.$t('Email'), helpText: this.$t('AccountSecurity.methodEmailHelp') },
        { value: 'mobile', label: this.$t('Mobile'), helpText: this.$t('AccountSecurity.methodMobileHelp') },
        { value: 'none', label: this.$t('None'), helpText: this.$t('AccountSecurity.methodNoneHelp') }
      ]
    },
    formattedLogs() {
      return this.logs
        .filter(l => {
          if (!this.filters.type) return true
          return !!this.filters.type.find(t => t === l.type)
        })
        .map(l => {
          return {
            ...l,
            formattedType: this.logTypes[l.type]
          }
        })
        .filter(l => {
          if (!this.query) return true
          return JSON.stringify(l).toLowerCase().includes(this.query.toLowerCase())
        })
    },
    logTypes() {
      return {
        'LOGGED_IN': this.$t('Login'),
        'LOGGED_OUT': this.$t('Logout'),
        'ACCOUNT_SWITCH': this.$t('AccountSecurity.accountSwitch'),
        'PASSWORD_CHANGED': this.$t('AccountSecurity.passwordChanged'),
        'PASSWORD_CHANGE_REQUEST': this.$t('AccountSecurity.passwordChangeRequest'),
        'LOGIN_FAIL_INACTIVITY': this.$t('AccountSecurity.failedLoginInactivity'),
        'LOGIN_FAIL_PASSWORD_CHANGE_REQUIRED': this.$t('AccountSecurity.failedLoginPasswordChangeRequired'),
        'LOGIN_FAIL_WRONG_PASSWORD': this.$t('AccountSecurity.failedLoginWrongPassword'),
        'LOGIN_FAIL_FAILED_LOGINS': this.$t('AccountSecurity.failedLoginFailedAttempts'),
        'LOCK_INACTIVITY': this.$t('AccountSecurity.accountLockedInactivity'),
        'LOCK_FAILED_LOGINS': this.$t('AccountSecurity.accountLockedFailedAttempts'),
        'UNLOCK_USER': this.$t('AccountSecurity.unlockedUser'),
        'LOG_TYPE_LOCK_PASSWORD_CHANGE_REQUIRED': this.$t('AccountSecurity.accountLockedPasswordChangeRequired')
      }
    },
    logTypeOptions() {
      return Object.keys(this.logTypes).map(k => {
        return {
          label: this.logTypes[k],
          value: k
        }
      })
    },
    secondaryActions() {
      const rv = [
        {
          label: this.$t('Notes'),
          onAction: () => this.eventHub.$emit('showAccountNotes', 'ACCOUNTSECURITY')
        }
      ]
      if (this.showLogs) {
        rv.push({
          label: this.$t('AccountSecurity.hideLogs'),
          onAction: this.toggleShowLogs
        })
      } else {
        rv.push({
          label: this.$t('AccountSecurity.showLogs'),
          onAction: this.toggleShowLogs
        })
      }
      return rv
    },
    columns() {
      return [
      {
        key: 'createdByName',
        label: this.$t('Name')
      },
      {
        key: 'formattedType',
        label: this.$t('Action')
      },
      {
        key: 'ipAddress',
        label: this.$t('IP Address')
      },
      {
        key: 'createdOn',
        label: this.$t('Date'),
        format: 'dateTimeShort'
      }]
    }, 
  },
  methods: {
    toggleShowLogs() {
      if (!this.showLogs) {
        this.showLogs = true
        this.logsLoading = true
        this.api.getCurrentAccountSecurityLogs()
          .then((data) => {
            this.logs = data
            this.logsLoading = false
          })
          .catch(this.apiCatch)
      } else {
        this.showLogs = false
        this.offset = 0
        this.filters = {}
        this.query = ''
      }
    },
    updateLimit(limit) {
      this.setTableLimit('tableLimit', limit)
      this.limit = limit
      this.offset = 0
    },
    save() {
      NProgress.start()
      this.saving = true
      this.api.updateCurrentAccountSecurity(this.accountSecurity)
        .then(() => {
          this.$ui.toast({ title: this.$t('Saved') })
          this.accountSecurityCopy = _.cloneDeep(this.accountSecurity)
        })
        .catch(this.apiCatch)
        .finally(() => {
          this.saving = false
          NProgress.done()
        })
    },
    enableWatchers() {
      this.$watch('twoFactorEnabled', (twoFactorEnabled) => {
        if (!twoFactorEnabled) {
          this.accountSecurity.twoFactorMethods = []
        }
      })
      this.$watch('passwordReuseEnabled', (newVal) => {
        if (newVal) {
          this.accountSecurity.securityPasswordReuse = _.clone(this.defaults.securityPasswordReuse)
        } else {
          this.accountSecurity.securityPasswordReuse = {}
        }
      })
      this.$watch('complexityMinLengthEnabled', (newVal) => {
        if (this.accountSecurity.securityPasswordComplexity) {
          if (newVal) {
            this.accountSecurity.securityPasswordComplexity.minimumLength = this.defaults.securityPasswordComplexity.minimumLength
          } else {
            delete this.accountSecurity.securityPasswordComplexity.minimumLength
          }
        }
      })
      this.$watch('passwordComplexityEnabled', (newVal) => {
        if (!newVal) {
          this.accountSecurity.securityPasswordComplexity = {}
          this.complexityMinLengthEnabled = false
        }
      })
      this.$watch('lockAccountsFailedEnabled', (newVal) => {
        if (newVal) {
          this.accountSecurity.securityLoginFailures = _.clone(this.defaults.securityLoginFailures)
        } else {
          this.accountSecurity.securityLoginFailures = {}
        }
      })
      this.$watch('lockAccountsNoLoginEnabled', (newVal) => {
        if (newVal) {
          this.accountSecurity.securityInactivityLockDays = _.clone(this.defaults.securityInactivityLockDays)
          this.accountSecurity.securityInactivityLockWarningDays = _.clone(this.defaults.securityInactivityLockWarningDays)
        } else {
          this.accountSecurity.securityInactivityLockDays = 0
          this.accountSecurity.securityInactivityLockWarningDays = 0
        }
      })
      this.$watch('forcePasswordChangeEnabled', (newVal) => {
        if (newVal) {
          this.accountSecurity.securityForcePasswordChangeDays = _.clone(this.defaults.securityForcePasswordChangeDays)
          this.accountSecurity.securityForcePasswordChangeWarningDays = _.clone(this.defaults.securityForcePasswordChangeWarningDays)
        } else {
          this.accountSecurity.securityForcePasswordChangeDays = 0
          this.accountSecurity.securityForcePasswordChangeWarningDays = 0
        }
      })
      this.$watch('autoLogoutEnabled', (newVal) => {
        if (newVal) {
          this.accountSecurity.securityAutomaticLogout = _.clone(this.defaults.securityAutomaticLogout)
        } else {
          this.accountSecurity.securityAutomaticLogout = {}
        }
      })
    }
  }
}

</script>

<style lang="scss">
#AccountSecurity {
  .checkbox {
    margin: 8px 5px 8px 3px;
  }
}
</style>
